import React, { useEffect, useState, useMemo } from 'react';
import { Tree, Spin, Empty, Input, Tag, Button, Tooltip } from 'antd';
import {
  FolderOutlined, FolderOpenOutlined, FileTextOutlined,
  ReloadOutlined, ExportOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

export default function GedDossiers() {
  const { t } = useTranslation('ged');
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  // Adresse d'OssanGED fournie par le backend : elle dépend de l'environnement
  // et ne doit pas être figée dans le bundle au moment du build.
  const [ossangedUrl, setOssangedUrl] = useState(null);

  const load = () => {
    setLoading(true);
    axiosInstance.get('/v1/ged/dossiers/arbre')
      .then(r => setDossiers(r.data?.data || []))
      // i18n.t et non le `t` du hook : load reste sans dépendance, l'effet de montage aussi.
      .catch(() => toast.error(i18n.t('ged:dossiers.chargementImpossible')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    axiosInstance.get('/v1/ged/portail/base-url')
      .then(r => setOssangedUrl(r.data?.url || null))
      .catch(() => setOssangedUrl(null));
  }, []);

  const ouvrirGed = (chemin = '') => {
    if (!ossangedUrl) { toast.error(t('dossiers.adresseIndisponible')); return; }
    window.open(`${ossangedUrl}${chemin}`, '_blank', 'noopener');
  };

  // Construction de l'arbre Ant Design : Sinistres → année → ET/TE → dossier
  const treeData = useMemo(() => {
    const filtered = search
      ? dossiers.filter(d =>
          (d.sinistreNumeroLocal || d.titre || '').toLowerCase().includes(search.toLowerCase())
        )
      : dossiers;

    const years = {};
    filtered.forEach(d => {
      const annee = d.annee || 'N/A';
      const type  = d.typeSinistreDir || 'AUTRE';
      if (!years[annee]) years[annee] = {};
      if (!years[annee][type]) years[annee][type] = [];
      years[annee][type].push(d);
    });

    return Object.keys(years)
      .sort((a, b) => b - a)
      .map(annee => ({
        key: `annee-${annee}`,
        title: <span style={{ fontWeight: 700, color: '#1a6b3c' }}>📁 {annee}</span>,
        children: Object.keys(years[annee]).sort().map(type => ({
          key: `annee-${annee}-${type}`,
          title: <span style={{ fontWeight: 600 }}>📂 {type}</span>,
          children: years[annee][type].map(d => ({
            key: d.ossanGedDossierTrackingId,
            isLeaf: true,
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileTextOutlined style={{ color: '#6b7280' }} />
                  <span style={{ fontSize: 13 }}>{d.sinistreNumeroLocal || d.titre}</span>
                </span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {d.ossanGedStoragePathId && (
                    <Tag color="green" style={{ fontSize: 10, margin: 0 }}>
                      ID SP: {d.ossanGedStoragePathId}
                    </Tag>
                  )}
                  {d.ossanGedStoragePathId && (
                    <Tooltip title={t('dossiers.voirDansOssanGed')}>
                      <Button
                        type="link"
                        size="small"
                        icon={<ExportOutlined />}
                        style={{ padding: '0 4px', color: '#1a6b3c' }}
                        onClick={e => {
                          e.stopPropagation();
                          ouvrirGed(`/#/documents?storage_path__id__in=${d.ossanGedStoragePathId}`);
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            ),
            data: d,
          })),
        })),
      }));
  }, [dossiers, search, t]);

  return (
    <Layout title={t('dossiers.titreLayout')}>
      <div className="cb-page-header">
        <div>
          <h1 className="cb-page-title">{t('dossiers.titre')}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button
            icon={<ExportOutlined />}
            style={{ borderColor: '#1a6b3c', color: '#1a6b3c' }}
            onClick={() => ouvrirGed()}
          >
            {t('dossiers.ouvrirOssanGed')}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading}
            style={{ borderColor: '#1a6b3c', color: '#1a6b3c' }}
          >
            {t('dossiers.actualiser')}
          </Button>
        </div>
      </div>

      <div className="cb-card">
        <Input.Search
          placeholder={t('dossiers.rechercher')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 380, marginBottom: 20 }}
        />

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : treeData.length === 0 ? (
          <Empty description={t('dossiers.aucunDossier')} />
        ) : (
          <Tree
            treeData={treeData}
            defaultExpandAll={dossiers.length < 30}
            showLine={{ showLeafIcon: false }}
            blockNode
            icon={({ expanded, isLeaf }) =>
              isLeaf ? null : expanded ? <FolderOpenOutlined /> : <FolderOutlined />
            }
            style={{ fontSize: 13 }}
          />
        )}

        <div style={{ marginTop: 16, fontSize: 12, color: '#9ca3af' }}>
          {t('dossiers.nombreDossiers', { count: dossiers.length })}
        </div>
      </div>
    </Layout>
  );
}
